/**
 * Sección "Clientes" del panel de Superadmin: lista de todos los municipios
 * dados de alta con el estado de la invitación de su admin principal, y un
 * botón para reenviarla cuando todavía no activó su cuenta. Se usa desde
 * pages/Superadmin.jsx, con el token de superadmin.
 *
 *   GET  /api/superadmin/municipios                         → la lista
 *   POST /api/superadmin/municipios/:id/reenviar-invitacion → token nuevo +
 *        plazo nuevo + el mismo mail del alta de cliente
 *   PUT  /api/superadmin/municipios/:id/dominio-propio      → asigna, cambia o
 *        quita el dominio propio del cliente (ej. licencias.munichaco.gob.ar),
 *        además de su subdominio de siempre
 *   POST /api/superadmin/municipios/:id/limites/vista-previa → solo lectura:
 *        cuántos activos tiene el cliente por rol frente a los límites que se
 *        quieren guardar, y a quiénes se podría desactivar
 *   PUT  /api/superadmin/municipios/:id/limites             → override por
 *        cliente del tope de administradores, operadores y diseños (en blanco
 *        = usa el límite general de la plataforma) y, si se pide, desactiva en
 *        la misma transacción al personal sobrante que se haya elegido
 *   PUT  /api/superadmin/municipios/:id/estado              → activo /
 *        suspendido / baja: corta (o devuelve) el acceso del cliente de
 *        inmediato, sesiones abiertas incluidas
 *
 * Estados (los calcula el backend, acá solo se muestran): activo (ya puso
 * su contraseña), pendiente (invitación enviada, dentro del plazo), vencido
 * (pasó el plazo sin activar). "Reenviar invitación" aparece solo en los
 * dos últimos. Además hay dos estados informativos, sin acción posible:
 * sin_admin (la base no tiene ningún admin cargado) y sin_base (el registro
 * existe pero su base no).
 *
 * Dominio propio: cada tarjeta muestra el dominio del cliente (si tiene) y un
 * botón para asignarlo/editarlo, con el formulario adentro de la misma tarjeta.
 * Quitarlo pide una confirmación en línea (no un diálogo nativo): ese dominio
 * deja de responder de inmediato. El error del backend (formato inválido,
 * dominio ya usado por otro cliente) se muestra adentro del formulario, que
 * queda abierto para corregir.
 *
 * Estado de acceso (Suspender / Dar de baja / Reactivar): la tarjeta muestra la
 * fila "Acceso" solo cuando el cliente está suspendido o dado de baja (el
 * badge de arriba es el de la invitación del admin y también dice "Activo":
 * mostrar dos "Activo" distintos en la misma tarjeta confunde). Suspender y
 * Dar de baja piden confirmación en línea (no un diálogo nativo) porque el
 * municipio pierde el acceso de inmediato; Reactivar es directo. Los clientes
 * protegidos (tumunicipio, miapp y el municipio del cron: `protegido` en la
 * respuesta del backend) no ofrecen ninguno de los dos, solo un aviso.
 *
 * Editar límites: mismo patrón visual que el dominio propio (formulario
 * adentro de la tarjeta, Guardar solo habilitado si cambió algo), pero como
 * estado independiente — se puede tener abierto el de dominio y el de
 * límites en la misma tarjeta a la vez, son dos cosas distintas. Tres inputs
 * numéricos (administradores / operadores / diseños); vacío = sin override,
 * usa el límite general (los números salen de `limites_generales` del listado,
 * no están escritos acá). Esto es el override por cliente — el límite EFECTIVO
 * que ve cada municipio sale de su propio backend (GET /api/personal y
 * GET /api/municipios/disenos), no de acá.
 *
 * Bajar un límite por debajo de lo que el cliente ya tiene: al tocar Guardar se
 * pide la vista previa; si algún rol (o los diseños) queda por encima, el
 * formulario muestra "tiene N activos, el nuevo límite es M, sobran X" y un
 * selector con el personal que se podría desactivar (últimos agregados
 * primero, ya tildados; el Admin Principal aparece fijo y sin tildar). Tres
 * salidas: Cancelar, "Guardar solo el límite" (no desactiva a nadie) y
 * "Guardar y desactivar a los marcados". Lo que se puede marcar lo decide el
 * backend, que además lo revalida todo al guardar. En los clientes protegidos
 * (tumunicipio, miapp, el del cron) no hay selector: solo se guarda el límite.
 * Los diseños son solo un aviso: bajar el tope no borra ninguno.
 *
 * Aviso de regularización: en esa misma confirmación hay una casilla "Enviar
 * aviso al Admin Principal" (el backend manda el destinatario —la regla única de
 * Admin Principal— y el texto exacto del mail en `vista.aviso`). Viene tildada
 * SOLO si hay personal marcado para desactivar (y sigue ese criterio mientras no
 * se toque a mano); si el Admin Principal no tiene email o todavía no activó
 * su cuenta, la casilla va deshabilitada y se explica por qué. El mail sale
 * después de guardar: si no sale, los límites y las desactivaciones igual
 * quedaron guardados y la tarjeta lo dice con un aviso amarillo (no verde).
 *
 * Claridad visual de la lista (solo presentación, no cambia datos ni permisos):
 *  - Las tarjetas de clientes suspendidos, de baja o sin base de datos se ven en
 *    escala de grises (`esApagada`), con su etiqueta de texto (SUSPENDIDO / BAJA /
 *    SIN BASE DE DATOS): el color nunca es la única señal. Los textos de esas
 *    tarjetas cumplen contraste AA (≥ 4,5:1) sobre el fondo gris. "Reactivar"
 *    conserva su color normal.
 *  - Los de baja no van en la lista principal: quedan en un grupo plegado al final,
 *    "Dados de baja (N)", cerrado por defecto (si no hay ninguno, no se muestra).
 *    Se abre solo justo después de dar de baja a alguien, para que se vea el
 *    resultado de la acción en su tarjeta. Los suspendidos siguen en la lista.
 *  - Los clientes internos (`interno` del backend: tumunicipio, miapp y el sandbox
 *    demo; no son clientes reales) llevan una etiqueta INTERNO, se cuentan aparte
 *    en el encabezado ("N clientes · M internos") y van después de los reales.
 *
 * Mismo estilo visual que Superadmin.jsx / SuperadminSeguridad.jsx.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { STROKE_DEGRADE, IconExito, IconAdvertencia, IconEmail, IconEditar, IconMunicipio, IconPersonal, IconDisenador } from './icons';
import { formatearFecha } from '../utils/fecha';
import '../styles/login.css';

const API_URL = process.env.REACT_APP_API_URL;

// Texto y colores de cada estado. activo=verde, pendiente=amarillo,
// vencido=rojo (misma paleta que las cajas de mensaje del panel); los dos
// informativos van en gris para no confundirse con un semáforo.
const ESTADOS = {
  activo: { texto: 'Activo', fondo: 'rgba(0, 217, 163, 0.14)', color: '#0f8a6d' },
  pendiente: { texto: 'Pendiente', fondo: 'rgba(243, 156, 18, 0.16)', color: '#9a6b00' },
  vencido: { texto: 'Vencido', fondo: 'rgba(231, 76, 60, 0.12)', color: '#c0392b' },
  sin_admin: { texto: 'Sin admin', fondo: '#eef0f2', color: '#5f6b73' },
  sin_base: { texto: 'Sin base de datos', fondo: '#eef0f2', color: '#5f6b73' },
};

const REENVIABLES = ['pendiente', 'vencido'];

// Estado de acceso del cliente (el backend lo calcula en `estado_cliente`).
const ACCESO = {
  activo: { texto: 'Activo', color: '#0f8a6d' },
  suspendido: { texto: 'Suspendido', color: '#9a6b00' },
  baja: { texto: 'Baja', color: '#c0392b' },
};

// Texto de la confirmación en línea de cada acción que corta el acceso.
const CONFIRMACION_ESTADO = {
  suspendido: {
    boton: 'Sí, suspender',
    enCurso: 'Suspendiendo...',
    texto: (c) => `${c.nombre} pierde el acceso de inmediato, sesiones abiertas incluidas: quien esté logueado sale en su próxima acción. Se puede reactivar cuando quieras.`,
  },
  baja: {
    boton: 'Sí, dar de baja',
    enCurso: 'Dando de baja...',
    texto: (c) => `${c.nombre} pierde el acceso de inmediato, sesiones abiertas incluidas: quien esté logueado sale en su próxima acción. Los datos no se borran y se puede reactivar si hace falta.`,
  },
};

// El backend manda los datetime como 'YYYY-MM-DD HH:MM:SS' (hora del
// servidor, Argentina). Se muestra tal cual, sin pasar por new Date(): así no
// hay corrimiento por huso horario del navegador (ver utils/fecha.js).
const formatearFechaHora = (valor) => {
  if (!valor) return '-';
  const [fecha, hora] = String(valor).split(' ');
  return hora ? `${formatearFecha(fecha)} ${hora.slice(0, 5)}` : formatearFecha(fecha);
};

// Guardar solo se habilita si hay algo para guardar: un texto distinto del
// dominio actual. En blanco no guarda (para eso está "Quitar dominio", que
// pide confirmación).
const dominioGuardable = (cliente, edicion) => {
  const valor = edicion.valor.trim();
  return !edicion.guardando && valor !== '' && valor.toLowerCase() !== (cliente.dominio_propio || '');
};

// Mínimo de cada límite (el mismo que valida el backend): un municipio sin
// administradores queda inmanejable y sin diseños no puede imprimir; los
// operadores sí pueden ser 0.
const LIMITES_CAMPOS = {
  maxAdmins: { minimo: 1, clave: 'max_admins' },
  maxOperadores: { minimo: 0, clave: 'max_operadores' },
  maxDisenos: { minimo: 1, clave: 'max_disenos' },
};

// Convierte lo que haya en el input a: null (en blanco, sin override), un
// entero >= minimo, o NaN si es inválido — así "Guardar" se puede deshabilitar
// sin intentar mandar algo que el backend igual va a rechazar.
const parsearLimite = (texto, minimo = 0) => {
  const valor = String(texto ?? '').trim();
  if (valor === '') return null;
  const n = Number(valor);
  return Number.isInteger(n) && n >= minimo ? n : NaN;
};

// Los tres valores del formulario ya parseados (null = sin override, NaN =
// inválido).
const limitesParseados = (edicion) => ({
  maxAdmins: parsearLimite(edicion.maxAdmins, LIMITES_CAMPOS.maxAdmins.minimo),
  maxOperadores: parsearLimite(edicion.maxOperadores, LIMITES_CAMPOS.maxOperadores.minimo),
  maxDisenos: parsearLimite(edicion.maxDisenos, LIMITES_CAMPOS.maxDisenos.minimo),
});

// Guardar solo se habilita si los tres valores son válidos y al menos uno
// cambió respecto a lo que ya tiene guardado el cliente.
const limitesGuardable = (cliente, edicion) => {
  if (edicion.guardando || edicion.calculando) return false;
  const v = limitesParseados(edicion);
  if (Object.values(v).some(Number.isNaN)) return false;
  return Object.entries(LIMITES_CAMPOS).some(([campo, { clave }]) => v[campo] !== (cliente[clave] ?? null));
};

// Cuerpo de los dos pedidos (vista previa y guardado).
const cuerpoLimites = (edicion) => {
  const v = limitesParseados(edicion);
  return { max_admins: v.maxAdmins, max_operadores: v.maxOperadores, max_disenos: v.maxDisenos };
};

const ROLES_LIMITES = [
  { rol: 'admin', titulo: 'Administradores', singular: 'administrador' },
  { rol: 'operador', titulo: 'Operadores', singular: 'operador' },
];

// ¿Va tildada la casilla del aviso? Si el backend dice que no hay a quién avisarle,
// nunca. Si no, lo que haya elegido el Superadmin a mano o, mientras no la haya
// tocado, tildada solo si hay personal marcado para desactivar.
const avisarEfectivo = (edicion) => {
  if (!edicion.vista?.aviso?.disponible) return false;
  if (edicion.avisar !== null && edicion.avisar !== undefined) return edicion.avisar;
  return edicion.seleccion.admin.length + edicion.seleccion.operador.length > 0;
};

const textoActivos = (n) => `${n} ${n === 1 ? 'activo' : 'activos'}`;

// Confirmación de una reducción: se muestra dentro del formulario de límites,
// en lugar de sus botones, cuando algún rol (o los diseños) queda por encima.
// `seleccion` = { admin: [ids], operador: [ids] }. No decide nada por su cuenta:
// los candidatos, los protegidos y los preseleccionados vienen del backend.
const PanelReduccion = ({ vista, seleccion, guardando, avisar, onAvisar, onAlternar, onSoloLimite, onDesactivar, onCancelar }) => {
  const aviso = vista.aviso;
  const seleccionados = seleccion.admin.length + seleccion.operador.length;
  const rolesExcedidos = ROLES_LIMITES.filter(({ rol }) => vista.roles[rol].sobran > 0);
  return (
    <div style={estilos.panelReduccion} data-testid="panel-reduccion">
      <div style={cajaMensaje('aviso')} role="alert">
        <span style={{ flexShrink: 0, marginTop: 1 }}><IconAdvertencia size={16} stroke="currentColor" /></span>
        <div>El cliente ya tiene más de lo que permiten los límites nuevos.</div>
      </div>

      {rolesExcedidos.map(({ rol, titulo, singular }) => {
        const r = vista.roles[rol];
        const elegidos = seleccion[rol];
        const tope = elegidos.length >= r.sobran;
        return (
          <div key={rol} style={estilos.bloqueRol}>
            <p style={estilos.tituloRol} data-testid={`aviso-${rol}`}>
              {titulo}: tiene {textoActivos(r.activos)}, el nuevo límite es {r.limite}, sobran {r.sobran}.
            </p>
            {vista.puede_desactivar ? (
              <>
                <p style={estilos.ayudaDominio}>
                  Elegí a quién desactivar (hasta {r.sobran}). Vienen marcados los últimos agregados.
                </p>
                <ul style={estilos.listaCandidatos}>
                  {r.candidatos.map((c) => {
                    const marcado = elegidos.includes(c.id);
                    const deshabilitado = guardando || c.protegido || (!marcado && tope);
                    return (
                      <li key={c.id} style={estilos.candidato(c.protegido)}>
                        <label style={estilos.labelCandidato(deshabilitado)}>
                          <input
                            type="checkbox"
                            checked={marcado}
                            disabled={deshabilitado}
                            onChange={() => onAlternar(rol, c.id)}
                            style={estilos.checkCandidato}
                          />
                          <span style={estilos.textoCandidato}>
                            <span style={estilos.nombreCandidato}>{c.nombre_completo || c.email || `Usuario ${c.id}`}</span>
                            {c.email && <span style={estilos.detalleCandidato}>{c.email}</span>}
                            <span style={estilos.detalleCandidato}>
                              Alta: {formatearFechaHora(c.fecha_alta)}
                              {c.estado === 'pendiente' ? ' · Invitación sin activar' : ''}
                              {c.protegido ? ` · ${c.motivo_proteccion}: no se desactiva` : ''}
                            </span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
                {r.no_alcanza && (
                  <p style={estilos.avisoNoAlcanza} data-testid={`no-alcanza-${rol}`}>
                    Aun desactivando a todos los que se pueden, quedarían {r.quedarian_como_minimo}{' '}
                    {r.quedarian_como_minimo === 1 ? `${singular} activo` : `${singular}es activos`} pero el límite es{' '}
                    {r.limite}: el resto está protegido.
                  </p>
                )}
              </>
            ) : null}
          </div>
        );
      })}

      {!vista.puede_desactivar && rolesExcedidos.length > 0 && (
        <p style={estilos.ayudaDominio} data-testid="aviso-protegido">
          {vista.cliente.nombre} es un municipio de demos o pruebas internas: se puede guardar el límite, pero desde el
          panel no se desactiva personal.
        </p>
      )}

      {vista.disenos.sobran > 0 && (
        <div style={estilos.bloqueRol}>
          <p style={estilos.tituloRol} data-testid="aviso-disenos">
            Diseños: tiene {vista.disenos.total}, el nuevo límite es {vista.disenos.limite}, sobran {vista.disenos.sobran}.
          </p>
          <p style={estilos.ayudaDominio}>
            No se borra ningún diseño: solo se bloquea crear o duplicar hasta que el municipio borre los que sobran.
          </p>
        </div>
      )}

      {vista.puede_desactivar && rolesExcedidos.length > 0 && (
        <p style={estilos.ayudaDominio}>
          Quien se desactiva ya no puede volver a iniciar sesión (se puede reactivar desde Personal si queda lugar). Si
          tiene una sesión abierta, sigue hasta que venza (hasta 12 horas).
        </p>
      )}

      {aviso && (
        <div style={estilos.bloqueAviso} data-testid="bloque-aviso">
          <label style={estilos.labelCandidato(!aviso.disponible || guardando)}>
            <input
              type="checkbox"
              checked={avisar}
              disabled={!aviso.disponible || guardando}
              onChange={(e) => onAvisar(e.target.checked)}
              style={estilos.checkCandidato}
              data-testid="casilla-aviso"
            />
            <span style={estilos.textoCandidato}>
              <span style={estilos.nombreCandidato}>Enviar aviso al Admin Principal</span>
              <span style={estilos.detalleCandidato} data-testid="detalle-aviso">
                {!aviso.disponible
                  ? 'No hay a quién avisarle.'
                  : avisar
                    ? 'Se enviará el mail de abajo después de guardar, con cualquiera de los dos botones de guardar.'
                    : 'No se enviará ningún mail.'}
              </span>
            </span>
          </label>
          {aviso.disponible ? (
            <div style={estilos.vistaMail} data-testid="vista-mail">
              <p style={estilos.lineaMail}><strong>Para:</strong> {aviso.destinatario.nombre ? `${aviso.destinatario.nombre} ` : ''}&lt;{aviso.destinatario.email}&gt;</p>
              <p style={estilos.lineaMail}><strong>Asunto:</strong> {aviso.asunto}</p>
              <p style={estilos.lineaMail}><strong>Las respuestas van a:</strong> {aviso.responder_a}</p>
              <p style={estilos.textoMail}>{aviso.texto}</p>
            </div>
          ) : (
            <p style={{ ...estilos.avisoNoAlcanza, margin: '0 12px 12px' }} data-testid="aviso-no-disponible">{aviso.explicacion}</p>
          )}
        </div>
      )}

      <div style={estilos.filaBotonesDominio}>
        {vista.puede_desactivar && rolesExcedidos.length > 0 && (
          <button
            type="button"
            style={estilos.botonReenviar(guardando || seleccionados === 0)}
            disabled={guardando || seleccionados === 0}
            onClick={onDesactivar}
          >
            {guardando ? 'Guardando...' : `Guardar y desactivar a los marcados (${seleccionados})`}
          </button>
        )}
        <button
          type="button"
          style={estilos.botonReenviar(guardando)}
          disabled={guardando}
          onClick={onSoloLimite}
        >
          Guardar solo el límite
        </button>
        <button type="button" style={estilos.botonSecundario} disabled={guardando} onClick={onCancelar}>
          Cancelar
        </button>
      </div>
    </div>
  );
};

// Una tarjeta "apagada" (gris): el cliente no está operando — suspendido, de baja
// o con la base de datos ilegible. Los clientes activos no cambian.
const esApagada = (c) => (c.estado_cliente && c.estado_cliente !== 'activo') || c.estado_invitacion === 'sin_base';
const esBaja = (c) => c.estado_cliente === 'baja';

// Plural simple para el contador del encabezado.
const cuenta = (n, singular, plural) => `${n} ${n === 1 ? singular : plural}`;

const SuperadminClientes = ({ token, activa, onSesionExpirada }) => {
  const [clientes, setClientes] = useState(null); // null = todavía no cargó
  // Límites generales (administradores, operadores y diseños: los que aplican a
  // cada cliente sin override propio) tal como los devuelve el backend en el
  // listado — null hasta que carga. Solo alimentan el texto de ayuda del
  // formulario de límites; el chequeo real vive en el backend.
  const [limitesGenerales, setLimitesGenerales] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [errorLista, setErrorLista] = useState('');
  const [reenviandoId, setReenviandoId] = useState(null);
  // Resultado del último reenvío de cada cliente, para mostrarlo en su propia
  // tarjeta: { [id]: { tipo: 'exito' | 'error', texto } }
  const [mensajes, setMensajes] = useState({});
  // Edición del dominio propio: una tarjeta a la vez. null = ninguna abierta.
  // { id, valor, guardando, error, confirmandoQuitar }
  const [edicion, setEdicion] = useState(null);
  // Edición de límites — estado independiente del de dominio (ver comentario de
  // cabecera). { id, maxAdmins, maxOperadores, maxDisenos, calculando,
  // guardando, error, vista, seleccion }; los tres max* son el texto crudo del
  // input, no un número ya parseado. `vista` = respuesta de la vista previa
  // cuando hay algo que confirmar (null = formulario normal) y `seleccion` =
  // { admin: [ids], operador: [ids] } lo que está tildado para desactivar.
  const [edicionLimites, setEdicionLimites] = useState(null);
  // Suspender / dar de baja: confirmación en línea, una tarjeta a la vez.
  // null = ninguna abierta. { id, estado: 'suspendido' | 'baja', guardando, error }
  const [confirmEstado, setConfirmEstado] = useState(null);
  // Cliente al que se le está pidiendo "Reactivar" (acción directa).
  const [reactivandoId, setReactivandoId] = useState(null);
  // Grupo plegado "Dados de baja (N)": cerrado por defecto.
  const [bajasAbiertas, setBajasAbiertas] = useState(false);
  // Descarta la respuesta de una carga vieja si ya se pidió otra (ej. se
  // reenvió una invitación mientras la lista anterior todavía volvía).
  const cargaActual = useRef(0);
  // Superadmin.jsx pasa onSesionExpirada como función nueva en cada render:
  // se guarda en un ref para que no entre en las dependencias de cargar()
  // (si no, el efecto de abajo volvería a pedir la lista en cada render).
  const sesionExpiradaRef = useRef(onSesionExpirada);
  sesionExpiradaRef.current = onSesionExpirada;

  const headers = { Authorization: `Bearer ${token}` };

  const cargar = useCallback(async () => {
    const numero = ++cargaActual.current;
    setCargando(true);
    setErrorLista('');
    try {
      const { data } = await axios.get(`${API_URL}/superadmin/municipios`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (numero !== cargaActual.current) return;
      setClientes(data.municipios || []);
      setLimitesGenerales(data.limites_generales || null);
    } catch (err) {
      if (numero !== cargaActual.current) return;
      if (err.response?.status === 401) {
        sesionExpiradaRef.current();
        return;
      }
      console.error('Error al listar clientes:', err);
      setErrorLista(err.response?.data?.mensaje || 'No se pudo cargar la lista de clientes. Probá de nuevo.');
    } finally {
      if (numero === cargaActual.current) setCargando(false);
    }
  }, [token]);

  // La sección queda montada aunque esté oculta (ver Superadmin.jsx), así que
  // se recarga cada vez que se vuelve a esta pestaña: el estado de las
  // invitaciones cambia solo (alguien activa, o se vence el plazo).
  useEffect(() => {
    if (activa) cargar();
  }, [activa, cargar]);

  const reenviar = async (cliente) => {
    setReenviandoId(cliente.id);
    setMensajes((prev) => ({ ...prev, [cliente.id]: undefined }));
    try {
      const { data } = await axios.post(
        `${API_URL}/superadmin/municipios/${cliente.id}/reenviar-invitacion`,
        {},
        { headers }
      );
      setMensajes((prev) => ({ ...prev, [cliente.id]: { tipo: 'exito', texto: data.mensaje } }));
      // Un "vencido" pasa a "pendiente" con el plazo nuevo.
      await cargar();
    } catch (err) {
      if (err.response?.status === 401) {
        sesionExpiradaRef.current();
        return;
      }
      console.error('Error al reenviar la invitación:', err);
      setMensajes((prev) => ({
        ...prev,
        [cliente.id]: {
          tipo: 'error',
          texto: err.response?.data?.mensaje || 'No se pudo reenviar la invitación. Probá de nuevo.',
        },
      }));
      // Si el estado cambió por otro lado (ej. ya había activado), la lista
      // se resincroniza para que el botón desaparezca.
      if (err.response?.status === 400) await cargar();
    } finally {
      setReenviandoId(null);
    }
  };

  const abrirEdicion = (cliente) => {
    setMensajes((prev) => ({ ...prev, [cliente.id]: undefined }));
    setEdicion({ id: cliente.id, valor: cliente.dominio_propio || '', guardando: false, error: '', confirmandoQuitar: false });
  };

  const cerrarEdicion = () => setEdicion(null);

  // dominio = texto a guardar, o '' para quitarlo.
  const guardarDominio = async (cliente, dominio) => {
    setEdicion((prev) => ({ ...prev, guardando: true, error: '' }));
    try {
      const { data } = await axios.put(
        `${API_URL}/superadmin/municipios/${cliente.id}/dominio-propio`,
        { dominio },
        { headers }
      );
      setEdicion(null);
      setMensajes((prev) => ({ ...prev, [cliente.id]: { tipo: 'exito', texto: data.mensaje } }));
      await cargar();
    } catch (err) {
      if (err.response?.status === 401) {
        sesionExpiradaRef.current();
        return;
      }
      console.error('Error al guardar el dominio propio:', err);
      // El formulario queda abierto con el error a la vista, para corregir.
      setEdicion((prev) => ({
        ...prev,
        guardando: false,
        confirmandoQuitar: false,
        error: err.response?.data?.mensaje || 'No se pudo guardar el dominio. Probá de nuevo.',
      }));
    }
  };

  const abrirEdicionLimites = (cliente) => {
    setMensajes((prev) => ({ ...prev, [cliente.id]: undefined }));
    setEdicionLimites({
      id: cliente.id,
      maxAdmins: cliente.max_admins ?? '',
      maxOperadores: cliente.max_operadores ?? '',
      maxDisenos: cliente.max_disenos ?? '',
      calculando: false,
      guardando: false,
      error: '',
      vista: null,
      seleccion: { admin: [], operador: [] },
      // null = automático (tildada solo si hay personal para desactivar);
      // true/false = el Superadmin la tocó a mano.
      avisar: null,
    });
  };

  const cerrarEdicionLimites = () => setEdicionLimites(null);

  // Cambiar un número invalida cualquier vista previa ya calculada (se pidió
  // para otros valores): hay que volver a tocar Guardar.
  const cambiarLimite = (campo, valor) =>
    setEdicionLimites((prev) => ({ ...prev, [campo]: valor, error: '', vista: null }));

  // Paso 1 del guardado: la vista previa (solo lectura). Sin nada por encima
  // del límite se guarda directo; con algo por encima se muestra la
  // confirmación en vez de guardar.
  const solicitarGuardadoLimites = async (cliente) => {
    setEdicionLimites((prev) => ({ ...prev, calculando: true, error: '', vista: null }));
    try {
      const { data } = await axios.post(
        `${API_URL}/superadmin/municipios/${cliente.id}/limites/vista-previa`,
        cuerpoLimites(edicionLimites),
        { headers }
      );
      if (!data.hay_excedente) {
        await guardarLimites(cliente, { admin: [], operador: [] });
        return;
      }
      setEdicionLimites((prev) => ({
        ...prev,
        calculando: false,
        vista: data,
        avisar: null,
        seleccion: {
          admin: [...data.roles.admin.preseleccionados],
          operador: [...data.roles.operador.preseleccionados],
        },
      }));
    } catch (err) {
      if (err.response?.status === 401) {
        sesionExpiradaRef.current();
        return;
      }
      console.error('Error al calcular la vista previa de los límites:', err);
      setEdicionLimites((prev) => ({
        ...prev,
        calculando: false,
        error: err.response?.data?.mensaje || 'No se pudo revisar el efecto de los límites. Probá de nuevo.',
      }));
    }
  };

  // Tildar / destildar a alguien del selector. El tope por rol (no más que los
  // que sobran) lo hace cumplir el propio selector y lo revalida el backend.
  const alternarCandidato = (rol, id) =>
    setEdicionLimites((prev) => {
      const actuales = prev.seleccion[rol];
      const nuevos = actuales.includes(id) ? actuales.filter((x) => x !== id) : [...actuales, id];
      return { ...prev, seleccion: { ...prev.seleccion, [rol]: nuevos } };
    });

  // Paso 2: guarda los límites y desactiva a `desactivar` (vacío = solo el
  // límite). Todo en una transacción del lado del backend.
  const guardarLimites = async (cliente, desactivar, avisar = false) => {
    setEdicionLimites((prev) => ({ ...prev, guardando: true, calculando: false, error: '' }));
    try {
      const { data } = await axios.put(
        `${API_URL}/superadmin/municipios/${cliente.id}/limites`,
        { ...cuerpoLimites(edicionLimites), desactivar, avisar },
        { headers }
      );
      setEdicionLimites(null);
      // Si se pidió el aviso y el mail no salió, lo guardado igual quedó
      // guardado: el resultado se muestra como aviso (amarillo), no como éxito.
      const tipo = data.mail_enviado === false ? 'aviso' : 'exito';
      setMensajes((prev) => ({ ...prev, [cliente.id]: { tipo, texto: data.mensaje } }));
      await cargar();
    } catch (err) {
      if (err.response?.status === 401) {
        sesionExpiradaRef.current();
        return;
      }
      console.error('Error al guardar los límites:', err);
      // El formulario queda abierto con el error a la vista. Si cambió algo en
      // el medio (409), la vista previa ya no vale: se descarta para que se
      // vuelva a calcular con lo que hay ahora.
      setEdicionLimites((prev) => ({
        ...prev,
        guardando: false,
        error: err.response?.data?.mensaje || 'No se pudo guardar los límites. Probá de nuevo.',
        vista: err.response?.data?.codigo === 'CAMBIO_EN_EL_MEDIO' ? null : prev.vista,
      }));
    }
  };

  const abrirConfirmEstado = (cliente, estado) => {
    setMensajes((prev) => ({ ...prev, [cliente.id]: undefined }));
    setConfirmEstado({ id: cliente.id, estado, guardando: false, error: '' });
  };

  // estado = 'suspendido' | 'baja' (vienen de la confirmación en línea) o
  // 'activo' (Reactivar, directo y sin confirmación).
  const cambiarEstado = async (cliente, estado) => {
    const conConfirmacion = estado !== 'activo';
    if (conConfirmacion) {
      setConfirmEstado((prev) => ({ ...prev, guardando: true, error: '' }));
    } else {
      setReactivandoId(cliente.id);
      setMensajes((prev) => ({ ...prev, [cliente.id]: undefined }));
    }
    try {
      const { data } = await axios.put(
        `${API_URL}/superadmin/municipios/${cliente.id}/estado`,
        { estado },
        { headers }
      );
      setConfirmEstado(null);
      setMensajes((prev) => ({ ...prev, [cliente.id]: { tipo: 'exito', texto: data.mensaje } }));
      // La tarjeta pasa al grupo de bajas: se abre para que el resultado se vea.
      if (estado === 'baja') setBajasAbiertas(true);
      await cargar();
    } catch (err) {
      if (err.response?.status === 401) {
        sesionExpiradaRef.current();
        return;
      }
      console.error('Error al cambiar el estado del cliente:', err);
      const texto = err.response?.data?.mensaje || 'No se pudo cambiar el estado. Probá de nuevo.';
      if (conConfirmacion) {
        // La confirmación queda abierta con el error a la vista.
        setConfirmEstado((prev) => ({ ...prev, guardando: false, error: texto }));
      } else {
        setMensajes((prev) => ({ ...prev, [cliente.id]: { tipo: 'error', texto } }));
      }
    } finally {
      setReactivandoId(null);
    }
  };

  if (clientes === null) {
    return (
      <div style={estilos.contenedor}>
        {errorLista ? (
          <>
            <div style={cajaMensaje('error')}>
              <span style={{ flexShrink: 0, marginTop: 1 }}><IconAdvertencia size={16} stroke="currentColor" /></span>
              <div>{errorLista}</div>
            </div>
            <button type="button" className="btn-ingresar" onClick={cargar} disabled={cargando}>
              {cargando ? 'Cargando...' : 'Reintentar'}
            </button>
          </>
        ) : (
          <p style={estilos.estadoVacio}>Cargando clientes...</p>
        )}
      </div>
    );
  }

  // Reales primero, después los internos, y los de baja aparte (grupo plegado).
  // Los suspendidos quedan en la lista principal.
  const vigentes = clientes.filter((c) => !esBaja(c));
  const bajas = clientes.filter(esBaja);
  const reales = vigentes.filter((c) => !c.interno);
  const internos = vigentes.filter((c) => c.interno);
  const principales = [...reales, ...internos];

  // Una tarjeta de cliente (la usan la lista principal y el grupo de bajas).
  const renderTarjeta = (c) => {
          const apagada = esApagada(c);
          const e = apagada ? estilosApagada : estilos;
          const estado = ESTADOS[c.estado_invitacion] || ESTADOS.sin_base;
          const puedeReenviar = c.activo && REENVIABLES.includes(c.estado_invitacion);
          const mensaje = mensajes[c.id];
          const enCurso = reenviandoId === c.id;
          // Etiqueta de acceso (SUSPENDIDO / BAJA) cuando el cliente no está activo; el
          // badge de la invitación del admin se muestra en los activos y, en los
          // apagados, solo cuando es informativo (sin admin / sin base de datos): un
          // "SUSPENDIDO" junto a un "ACTIVO" de la invitación confunde.
          const accesoApagado = c.estado_cliente && c.estado_cliente !== 'activo';
          const mostrarBadgeInvitacion = !accesoApagado || ['sin_base', 'sin_admin'].includes(c.estado_invitacion);
          return (
            <li
              key={c.id}
              style={e.tarjeta}
              data-estado={c.estado_invitacion}
              data-acceso={c.estado_cliente || 'activo'}
              data-apagada={apagada ? 'si' : 'no'}
              data-interno={c.interno ? 'si' : 'no'}
            >
              <div style={e.filaSuperior}>
                <div style={e.bloqueNombre}>
                  <p style={e.nombre}>{c.nombre}</p>
                  <p style={e.subdominio}>
                    {c.subdominio}
                    {c.interno && <span style={e.etiquetaInterno} data-testid="etiqueta-interno">Interno</span>}
                  </p>
                </div>
                <div style={e.badges}>
                  {accesoApagado && (
                    <span style={e.badgeAcceso(c.estado_cliente)} data-testid="badge-acceso">
                      {(ACCESO[c.estado_cliente] || ACCESO.baja).texto}
                    </span>
                  )}
                  {mostrarBadgeInvitacion && <span style={e.badge(estado)} data-testid="badge-invitacion">{estado.texto}</span>}
                </div>
              </div>

              <dl style={e.datos}>
                <div style={e.dato}>
                  <dt style={e.datoTitulo}>Alta</dt>
                  <dd style={e.datoValor}>{formatearFechaHora(c.fecha_alta)}</dd>
                </div>
                {c.admin_email && (
                  <div style={e.dato}>
                    <dt style={e.datoTitulo}>Admin</dt>
                    <dd style={e.datoValor}>
                      {c.admin_email}
                      {!mostrarBadgeInvitacion ? ` · invitación ${estado.texto.toLowerCase()}` : ''}
                    </dd>
                  </div>
                )}
                <div style={e.dato}>
                  <dt style={e.datoTitulo}>Dominio</dt>
                  <dd style={c.dominio_propio ? e.datoValor : e.datoValorApagado}>
                    {c.dominio_propio || 'Sin dominio propio'}
                  </dd>
                </div>
                <div style={e.dato}>
                  <dt style={e.datoTitulo}>Límites</dt>
                  <dd style={(c.max_admins != null || c.max_operadores != null || c.max_disenos != null) ? e.datoValor : e.datoValorApagado}>
                    {(c.max_admins != null || c.max_operadores != null || c.max_disenos != null)
                      ? `Admins: ${c.max_admins ?? 'general'} · Operadores: ${c.max_operadores ?? 'general'} · Diseños: ${c.max_disenos ?? 'general'}`
                      : 'Usa el límite general'}
                  </dd>
                </div>
                {REENVIABLES.includes(c.estado_invitacion) && (
                  <div style={e.dato}>
                    <dt style={e.datoTitulo}>
                      {c.estado_invitacion === 'vencido' ? 'Venció' : 'Vence'}
                    </dt>
                    <dd style={e.datoValor}>{formatearFechaHora(c.invitacion_expira)}</dd>
                  </div>
                )}
              </dl>

              {puedeReenviar && (
                <button
                  type="button"
                  onClick={() => reenviar(c)}
                  disabled={reenviandoId !== null}
                  style={e.botonReenviar(reenviandoId !== null)}
                >
                  <IconEmail size={15} stroke="currentColor" />
                  {enCurso ? 'Enviando...' : 'Reenviar invitación'}
                </button>
              )}

              {edicion?.id === c.id ? (
                <form
                  style={e.formDominio}
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (dominioGuardable(c, edicion)) guardarDominio(c, edicion.valor);
                  }}
                >
                  <label htmlFor={`dominio-${c.id}`} style={e.labelDominio}>Dominio propio</label>
                  <div className="input-wrapper">
                    <span className="input-icon"><IconMunicipio size={16} stroke={STROKE_DEGRADE} /></span>
                    <input
                      id={`dominio-${c.id}`}
                      type="text"
                      inputMode="url"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      autoFocus
                      placeholder="licencias.munichaco.gob.ar"
                      value={edicion.valor}
                      onChange={(e) => setEdicion((prev) => ({ ...prev, valor: e.target.value, error: '' }))}
                      className="form-input"
                      style={{ paddingRight: 14 }}
                      disabled={edicion.guardando}
                    />
                  </div>
                  <p style={e.ayudaDominio}>
                    Solo el dominio, sin https://. El subdominio de siempre ({c.subdominio}) sigue funcionando.
                    Para que el dominio nuevo responda en internet hay que apuntar su DNS al servidor y sumarlo a
                    Nginx con su certificado.
                  </p>

                  {edicion.error && (
                    <div style={cajaMensaje('error')} role="alert">
                      <span style={{ flexShrink: 0, marginTop: 1 }}><IconAdvertencia size={16} stroke="currentColor" /></span>
                      <div>{edicion.error}</div>
                    </div>
                  )}

                  {edicion.confirmandoQuitar ? (
                    <div style={cajaMensaje('aviso')} role="alert">
                      <span style={{ flexShrink: 0, marginTop: 1 }}><IconAdvertencia size={16} stroke="currentColor" /></span>
                      <div>
                        <p style={{ margin: '0 0 10px' }}>
                          {c.dominio_propio} va a dejar de responder de inmediato. El cliente sigue entrando por{' '}
                          {c.subdominio}.
                        </p>
                        <div style={e.filaBotonesDominio}>
                          <button
                            type="button"
                            style={e.botonPeligro(edicion.guardando)}
                            disabled={edicion.guardando}
                            onClick={() => guardarDominio(c, '')}
                          >
                            {edicion.guardando ? 'Quitando...' : 'Sí, quitar dominio'}
                          </button>
                          <button
                            type="button"
                            style={e.botonSecundario}
                            disabled={edicion.guardando}
                            onClick={() => setEdicion((prev) => ({ ...prev, confirmandoQuitar: false }))}
                          >
                            No
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={e.filaBotonesDominio}>
                      <button
                        type="submit"
                        style={e.botonReenviar(!dominioGuardable(c, edicion))}
                        disabled={!dominioGuardable(c, edicion)}
                      >
                        {edicion.guardando ? 'Guardando...' : 'Guardar'}
                      </button>
                      <button type="button" style={e.botonSecundario} disabled={edicion.guardando} onClick={cerrarEdicion}>
                        Cancelar
                      </button>
                      {c.dominio_propio && (
                        <button
                          type="button"
                          style={e.botonQuitar}
                          disabled={edicion.guardando}
                          onClick={() => setEdicion((prev) => ({ ...prev, confirmandoQuitar: true, error: '' }))}
                        >
                          Quitar dominio
                        </button>
                      )}
                    </div>
                  )}
                </form>
              ) : (
                <button
                  type="button"
                  className="link-ayuda"
                  onClick={() => abrirEdicion(c)}
                  style={{ ...linkBoton, ...(apagada ? { color: '#46535b' } : {}), alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <IconEditar size={14} stroke="currentColor" />
                  {c.dominio_propio ? 'Editar dominio propio' : 'Asignar dominio propio'}
                </button>
              )}

              {edicionLimites?.id === c.id ? (
                <form
                  style={e.formDominio}
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (limitesGuardable(c, edicionLimites) && !edicionLimites.vista) solicitarGuardadoLimites(c);
                  }}
                >
                  <label style={e.labelDominio}>Editar límites</label>
                  <div style={e.filaLimites}>
                    {[
                      { campo: 'maxAdmins', etiqueta: 'Administradores', Icono: IconPersonal, minimo: LIMITES_CAMPOS.maxAdmins.minimo },
                      { campo: 'maxOperadores', etiqueta: 'Operadores', Icono: IconPersonal, minimo: LIMITES_CAMPOS.maxOperadores.minimo },
                      { campo: 'maxDisenos', etiqueta: 'Diseños', Icono: IconDisenador, minimo: LIMITES_CAMPOS.maxDisenos.minimo },
                    ].map(({ campo, etiqueta, Icono, minimo }) => (
                      <div key={campo} style={e.campoLimite}>
                        <label htmlFor={`limite-${campo}-${c.id}`} style={e.labelLimite}>{etiqueta}</label>
                        <div className="input-wrapper">
                          <span className="input-icon"><Icono size={16} stroke={STROKE_DEGRADE} /></span>
                          <input
                            id={`limite-${campo}-${c.id}`}
                            type="number"
                            min={minimo}
                            step="1"
                            inputMode="numeric"
                            placeholder="General"
                            value={edicionLimites[campo]}
                            onChange={(e) => cambiarLimite(campo, e.target.value)}
                            className="form-input"
                            style={{ paddingRight: 14 }}
                            disabled={edicionLimites.guardando || edicionLimites.calculando}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <p style={e.ayudaDominio}>
                    En blanco para cada uno = usa el límite general de la plataforma
                    {limitesGenerales
                      ? ` (hoy ${limitesGenerales.max_admins} administradores, ${limitesGenerales.max_operadores} operadores y ${limitesGenerales.max_disenos} diseños)`
                      : ''}
                    . Mínimo: 1 administrador, 0 operadores, 1 diseño.
                  </p>

                  {edicionLimites.error && (
                    <div style={cajaMensaje('error')} role="alert">
                      <span style={{ flexShrink: 0, marginTop: 1 }}><IconAdvertencia size={16} stroke="currentColor" /></span>
                      <div>{edicionLimites.error}</div>
                    </div>
                  )}

                  {edicionLimites.vista ? (
                    <PanelReduccion
                      vista={edicionLimites.vista}
                      seleccion={edicionLimites.seleccion}
                      guardando={edicionLimites.guardando}
                      avisar={avisarEfectivo(edicionLimites)}
                      onAvisar={(valor) => setEdicionLimites((prev) => ({ ...prev, avisar: valor }))}
                      onAlternar={alternarCandidato}
                      onSoloLimite={() => guardarLimites(c, { admin: [], operador: [] }, avisarEfectivo(edicionLimites))}
                      onDesactivar={() => guardarLimites(c, edicionLimites.seleccion, avisarEfectivo(edicionLimites))}
                      onCancelar={() => setEdicionLimites((prev) => ({ ...prev, vista: null, error: '' }))}
                    />
                  ) : (
                    <div style={e.filaBotonesDominio}>
                      <button
                        type="submit"
                        style={e.botonReenviar(!limitesGuardable(c, edicionLimites))}
                        disabled={!limitesGuardable(c, edicionLimites)}
                      >
                        {edicionLimites.calculando ? 'Revisando...' : edicionLimites.guardando ? 'Guardando...' : 'Guardar'}
                      </button>
                      <button
                        type="button"
                        style={e.botonSecundario}
                        disabled={edicionLimites.guardando || edicionLimites.calculando}
                        onClick={cerrarEdicionLimites}
                      >
                        Cancelar
                      </button>
                    </div>
                  )}
                </form>
              ) : (
                <button
                  type="button"
                  className="link-ayuda"
                  onClick={() => abrirEdicionLimites(c)}
                  style={{ ...linkBoton, ...(apagada ? { color: '#46535b' } : {}), alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <IconEditar size={14} stroke="currentColor" />
                  Editar límites
                </button>
              )}

              {c.estado_cliente !== 'activo' ? (
                <button
                  type="button"
                  className="link-ayuda"
                  onClick={() => cambiarEstado(c, 'activo')}
                  disabled={reactivandoId !== null}
                  style={{ ...linkBoton, alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <IconExito size={14} stroke="currentColor" />
                  {reactivandoId === c.id ? 'Reactivando...' : 'Reactivar'}
                </button>
              ) : c.protegido ? (
                <p style={e.ayudaDominio}>
                  Cliente protegido: no se puede suspender ni dar de baja (es de demo/pruebas del sistema).
                </p>
              ) : confirmEstado?.id === c.id ? (
                <div style={cajaMensaje('aviso')} role="alert">
                  <span style={{ flexShrink: 0, marginTop: 1 }}><IconAdvertencia size={16} stroke="currentColor" /></span>
                  <div>
                    <p style={{ margin: '0 0 10px' }}>{CONFIRMACION_ESTADO[confirmEstado.estado].texto(c)}</p>
                    {confirmEstado.error && (
                      <p style={{ margin: '0 0 10px', color: '#c0392b', fontWeight: 600 }}>{confirmEstado.error}</p>
                    )}
                    <div style={e.filaBotonesDominio}>
                      <button
                        type="button"
                        style={e.botonPeligro(confirmEstado.guardando)}
                        disabled={confirmEstado.guardando}
                        onClick={() => cambiarEstado(c, confirmEstado.estado)}
                      >
                        {confirmEstado.guardando
                          ? CONFIRMACION_ESTADO[confirmEstado.estado].enCurso
                          : CONFIRMACION_ESTADO[confirmEstado.estado].boton}
                      </button>
                      <button
                        type="button"
                        style={e.botonSecundario}
                        disabled={confirmEstado.guardando}
                        onClick={() => setConfirmEstado(null)}
                      >
                        No
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={e.filaBotonesDominio}>
                  <button
                    type="button"
                    className="link-ayuda"
                    onClick={() => abrirConfirmEstado(c, 'suspendido')}
                    style={{ ...linkBoton, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    <IconAdvertencia size={14} stroke="currentColor" />
                    Suspender
                  </button>
                  <button
                    type="button"
                    className="link-ayuda"
                    onClick={() => abrirConfirmEstado(c, 'baja')}
                    style={{ ...linkBoton, display: 'inline-flex', alignItems: 'center', gap: 6, color: '#c0392b' }}
                  >
                    <IconAdvertencia size={14} stroke="currentColor" />
                    Dar de baja
                  </button>
                </div>
              )}

              {mensaje && (
                <div style={cajaMensaje(mensaje.tipo)} role={mensaje.tipo === 'exito' ? 'status' : 'alert'}>
                  <span style={{ flexShrink: 0, marginTop: 1 }}>
                    {mensaje.tipo === 'exito'
                      ? <IconExito size={16} stroke="currentColor" />
                      : <IconAdvertencia size={16} stroke="currentColor" />}
                  </span>
                  <div>{mensaje.texto}</div>
                </div>
              )}
            </li>
          );
  };

  return (
    <div style={estilos.contenedor}>
      <div style={estilos.encabezado}>
        <p style={subtituloSeccion}>
          {cuenta(reales.length, 'cliente', 'clientes')}
          {internos.length > 0 ? ` · ${cuenta(internos.length, 'interno', 'internos')}` : ''}
        </p>
        <button type="button" className="link-ayuda" onClick={cargar} disabled={cargando} style={linkBoton}>
          {cargando ? 'Actualizando...' : 'Actualizar'}
        </button>
      </div>

      {errorLista && (
        <div style={cajaMensaje('error')} role="alert">
          <span style={{ flexShrink: 0, marginTop: 1 }}><IconAdvertencia size={16} stroke="currentColor" /></span>
          <div>{errorLista}</div>
        </div>
      )}

      {clientes.length === 0 && <p style={estilos.estadoVacio}>Todavía no hay clientes dados de alta.</p>}

      <ul style={estilos.lista}>{principales.map(renderTarjeta)}</ul>

      {bajas.length > 0 && (
        <div data-testid="grupo-bajas">
          <button
            type="button"
            id="bajas-toggle"
            aria-expanded={bajasAbiertas}
            aria-controls="bajas-lista"
            onClick={() => setBajasAbiertas((v) => !v)}
            style={estilos.grupoBajasBoton}
          >
            <span>Dados de baja ({bajas.length})</span>
            <span aria-hidden="true" style={estilos.grupoBajasFlecha(bajasAbiertas)}>›</span>
          </button>
          {bajasAbiertas && (
            <ul id="bajas-lista" style={{ ...estilos.lista, marginTop: 12 }}>{bajas.map(renderTarjeta)}</ul>
          )}
        </div>
      )}
    </div>
  );
};

// --- estilos puntuales de esta sección (el resto sale de login.css) ---
const subtituloSeccion = {
  margin: 0,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: '#7f8c8d',
  fontFamily: 'var(--font-header)',
};

const linkBoton = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  font: 'inherit',
  fontSize: 13,
  padding: 4,
};

const estilos = {
  contenedor: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  encabezado: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  estadoVacio: {
    margin: 0,
    padding: '24px 0',
    textAlign: 'center',
    fontSize: 13,
    color: '#7f8c8d',
  },
  lista: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  tarjeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: 16,
    background: '#f8f9fa',
    border: '2px solid #e0e0e0',
    borderRadius: 12,
  },
  // flexWrap: en pantallas angostas el badge (que no se achica) baja debajo
  // del nombre en vez de robarle el ancho y cortarlo a mitad de palabra.
  filaSuperior: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
  },
  bloqueNombre: {
    flex: '1 1 150px',
    minWidth: 0,
  },
  nombre: {
    margin: 0,
    fontSize: 15,
    fontWeight: 700,
    fontFamily: 'var(--font-header)',
    color: '#2c3e50',
    overflowWrap: 'break-word',
  },
  subdominio: {
    margin: '2px 0 0',
    fontSize: 12,
    color: '#7f8c8d',
    overflowWrap: 'anywhere',
  },
  badge: (estado) => ({
    flexShrink: 0,
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    fontFamily: 'var(--font-header)',
    letterSpacing: '0.3px',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    background: estado.fondo,
    color: estado.color,
  }),
  datos: {
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  dato: {
    display: 'flex',
    gap: 8,
    fontSize: 12,
    lineHeight: 1.4,
  },
  datoTitulo: {
    flex: '0 0 52px',
    color: '#7f8c8d',
  },
  datoValor: {
    margin: 0,
    minWidth: 0,
    color: '#2c3e50',
    overflowWrap: 'anywhere',
  },
  datoValorApagado: {
    margin: 0,
    minWidth: 0,
    color: '#95a5a6',
    overflowWrap: 'anywhere',
  },
  formDominio: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: 14,
    background: '#ffffff',
    border: '2px solid #e0e0e0',
    borderRadius: 12,
  },
  labelDominio: {
    fontSize: 13,
    fontWeight: 600,
    color: '#2c3e50',
    fontFamily: 'var(--font-header)',
  },
  ayudaDominio: {
    margin: 0,
    fontSize: 12,
    color: '#7f8c8d',
    lineHeight: 1.45,
  },
  // Dos inputs lado a lado en pantallas anchas; en mobile (flexWrap) cada uno
  // pasa a ocupar el ancho completo, igual que el resto de los formularios.
  filaLimites: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
  },
  campoLimite: {
    flex: '1 1 140px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  labelLimite: {
    fontSize: 12,
    color: '#7f8c8d',
  },
  // --- claridad visual de la lista ---
  badges: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: 6,
    maxWidth: '100%',
  },
  // Etiqueta de acceso de un cliente suspendido / de baja: gris con borde (no
  // depende del color para leerse: dice el estado con texto).
  badgeAcceso: (acceso) => ({
    flexShrink: 0,
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    fontFamily: 'var(--font-header)',
    letterSpacing: '0.3px',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    background: acceso === 'suspendido' ? '#dde2e5' : '#cfd5d9',
    color: acceso === 'suspendido' ? '#38434a' : '#2f3a41',
    border: '1px solid #aeb7bd',
  }),
  etiquetaInterno: {
    display: 'inline-block',
    marginLeft: 8,
    padding: '1px 7px',
    border: '1px solid #b6c0c5',
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    color: '#56626a',
    fontFamily: 'var(--font-header)',
    verticalAlign: 'middle',
  },
  grupoBajasBoton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '12px 16px',
    background: 'transparent',
    color: '#46535b',
    border: '2px dashed #cdd3d7',
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 700,
    fontFamily: 'var(--font-header)',
    cursor: 'pointer',
    textAlign: 'left',
  },
  grupoBajasFlecha: (abierto) => ({
    display: 'inline-block',
    fontSize: 20,
    lineHeight: 1,
    transform: abierto ? 'rotate(90deg)' : 'none',
    transition: 'transform 0.15s',
  }),
  // Confirmación de una reducción de límites (PanelReduccion).
  panelReduccion: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  bloqueRol: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  tituloRol: {
    margin: 0,
    fontSize: 13,
    fontWeight: 700,
    color: '#2c3e50',
    fontFamily: 'var(--font-header)',
    lineHeight: 1.4,
  },
  listaCandidatos: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  candidato: (protegido) => ({
    border: '1px solid #e0e0e0',
    borderRadius: 10,
    background: protegido ? '#f4f6f7' : '#ffffff',
  }),
  labelCandidato: (deshabilitado) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '10px 12px',
    cursor: deshabilitado ? 'not-allowed' : 'pointer',
    opacity: deshabilitado ? 0.75 : 1,
  }),
  checkCandidato: {
    flexShrink: 0,
    width: 18,
    height: 18,
    marginTop: 2,
    accentColor: '#0f8a6d',
  },
  textoCandidato: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  nombreCandidato: {
    fontSize: 13,
    fontWeight: 600,
    color: '#2c3e50',
    overflowWrap: 'anywhere',
  },
  detalleCandidato: {
    fontSize: 12,
    color: '#7f8c8d',
    overflowWrap: 'anywhere',
    lineHeight: 1.4,
  },
  bloqueAviso: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    border: '1px solid #e0e0e0',
    borderRadius: 10,
    background: '#ffffff',
  },
  vistaMail: {
    margin: '0 12px 12px',
    padding: '10px 12px',
    background: '#f4f6f7',
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  lineaMail: {
    margin: 0,
    fontSize: 12,
    color: '#5f6b73',
    lineHeight: 1.4,
    overflowWrap: 'anywhere',
  },
  textoMail: {
    margin: '6px 0 0',
    fontSize: 12,
    color: '#2c3e50',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
  avisoNoAlcanza: {
    margin: 0,
    fontSize: 12,
    color: '#9a6b00',
    lineHeight: 1.45,
  },
  filaBotonesDominio: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
  },
  botonSecundario: {
    padding: '10px 14px',
    background: 'transparent',
    color: '#5f6b73',
    border: '2px solid #dfe4e8',
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'var(--font-header)',
    cursor: 'pointer',
  },
  botonQuitar: {
    marginLeft: 'auto',
    padding: '10px 6px',
    background: 'none',
    border: 'none',
    color: '#c0392b',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'var(--font-header)',
    cursor: 'pointer',
  },
  botonPeligro: (deshabilitado) => ({
    padding: '10px 14px',
    background: '#c0392b',
    color: '#ffffff',
    border: 'none',
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'var(--font-header)',
    cursor: deshabilitado ? 'not-allowed' : 'pointer',
    opacity: deshabilitado ? 0.6 : 1,
  }),
  botonReenviar: (deshabilitado) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    alignSelf: 'flex-start',
    padding: '10px 16px',
    background: '#ffffff',
    color: 'var(--color-turquesa)',
    border: '2px solid var(--color-turquesa)',
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'var(--font-header)',
    cursor: deshabilitado ? 'not-allowed' : 'pointer',
    opacity: deshabilitado ? 0.6 : 1,
    transition: 'var(--transition)',
  }),
};

// Variante en escala de grises de la tarjeta (ver esApagada). Se arma sobre
// `estilos`, así que todo lo que no se redefine acá queda igual. Colores
// elegidos para ≥ 4,5:1 de contraste sobre el fondo #e9ecee.
const estilosApagada = {
  ...estilos,
  tarjeta: { ...estilos.tarjeta, background: '#e9ecee', border: '2px solid #cdd3d7' },
  nombre: { ...estilos.nombre, color: '#3f4a51' },
  subdominio: { ...estilos.subdominio, color: '#56626a' },
  datoTitulo: { ...estilos.datoTitulo, color: '#56626a' },
  datoValor: { ...estilos.datoValor, color: '#3f4a51' },
  datoValorApagado: { ...estilos.datoValorApagado, color: '#5c6870' },
  badge: (estado) => ({ ...estilos.badge(estado), border: '1px solid #aeb7bd' }),
};

const cajaMensaje = (tipo) => {
  const colores = {
    exito: { fondo: 'rgba(0, 217, 163, 0.1)', color: '#0f8a6d', borde: '#00d9a3' },
    aviso: { fondo: 'rgba(243, 156, 18, 0.12)', color: '#9a6b00', borde: '#f39c12' },
    error: { fondo: 'rgba(231, 76, 60, 0.1)', color: '#e74c3c', borde: '#e74c3c' },
  };
  const c = colores[tipo] || colores.aviso;
  return {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    background: c.fondo,
    color: c.color,
    padding: '12px 14px',
    borderRadius: 12,
    fontSize: 13,
    borderLeft: `4px solid ${c.borde}`,
    lineHeight: 1.45,
    // Un texto largo sin espacios (ej. un dominio en el mensaje de error) no
    // tiene que salirse de la caja en pantallas angostas.
    overflowWrap: 'anywhere',
  };
};

export default SuperadminClientes;
